import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { HelpCircle, CheckCircle2, Package } from 'lucide-react';

interface QA {
  id: string;
  productId: string;
  question: string;
  answer?: string;
  customerName: string;
  createdAt: any;
  productName?: string;
}

export function QAManager({ vendorId }: { vendorId?: string }) {
  const [qas, setQas] = useState<QA[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (!vendorId) return;

    const q = query(
      collection(db, 'product_qa'),
      where('vendorId', '==', vendorId)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const fetchedQas = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QA));
      
      // Fetch product names for context
      const enrichedQas = await Promise.all(fetchedQas.map(async (qa) => {
        if (!qa.productId) return qa;
        try {
          const pDoc = await getDoc(doc(db, 'products', qa.productId));
          if (pDoc.exists()) {
            return { ...qa, productName: pDoc.data().name };
          }
        } catch (e) {
          console.error(e);
        }
        return qa;
      }));

      // Sort with unanswered first, then by date desc
      enrichedQas.sort((a, b) => {
        if (!a.answer && b.answer) return -1;
        if (a.answer && !b.answer) return 1;
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setQas(enrichedQas);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [vendorId]);

  const handleReply = async (qaId: string) => {
    const answer = replyText[qaId];
    if (!answer?.trim()) return;

    try {
      await updateDoc(doc(db, 'product_qa', qaId), {
        answer: answer.trim(),
        answeredAt: serverTimestamp()
      });
      setReplyText(prev => {
        const next = { ...prev };
        delete next[qaId];
        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
      <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
        <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
          <HelpCircle className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
          Product Q&A
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">
          Answer public customer questions about your products.
        </p>
      </div>

      <div className="p-8">
        {qas.length === 0 ? (
          <div className="text-center py-24 bg-neutral-50 dark:bg-neutral-800/10 rounded-2xl border border-neutral-100 dark:border-neutral-800">
             <HelpCircle className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4 stroke-[1.5]" />
             <h3 className="text-lg font-medium text-neutral-900 dark:text-white">No questions yet</h3>
             <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 max-w-sm mx-auto font-light">
                When customers ask questions about your products, they will appear here for you to answer publicly.
             </p>
          </div>
        ) : (
          <div className="space-y-6">
            {qas.map(qa => (
              <div key={qa.id} className={`p-6 rounded-2xl border ${!qa.answer ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30' : 'bg-neutral-50 dark:bg-neutral-800/20 border-neutral-100 dark:border-neutral-800'}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                       <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-md ${qa.answer ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                         {qa.answer ? 'Answered' : 'Needs Answer'}
                       </span>
                       {qa.productName && (
                         <span className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                           <Package className="w-3 h-3" /> {qa.productName}
                         </span>
                       )}
                    </div>
                    <p className="text-base font-medium text-neutral-900 dark:text-white">{qa.question}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Asked by {qa.customerName} {qa.createdAt?.toDate ? `on ${qa.createdAt.toDate().toLocaleDateString()}` : ''}</p>
                  </div>
                </div>

                {!qa.answer ? (
                  <div className="mt-4 flex gap-3">
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-xl text-sm bg-white dark:bg-neutral-950 focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white transition-shadow text-neutral-900 dark:text-white"
                      placeholder="Type your public answer..."
                      value={replyText[qa.id] || ''}
                      onChange={e => setReplyText({ ...replyText, [qa.id]: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleReply(qa.id);
                      }}
                    />
                    <button
                      onClick={() => handleReply(qa.id)}
                      disabled={!replyText[qa.id]?.trim()}
                      className="px-6 py-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-sm font-medium rounded-xl hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50"
                    >
                      Reply
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 p-4 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-100 dark:border-neutral-800">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white mb-1 flex items-center gap-2">
                       <CheckCircle2 className="w-4 h-4 text-green-500" /> Your Answer
                    </p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">{qa.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
